/*
 * Photos are made smaller before upload (w4-01 fix 12): a phone photo is 3–6 MB, and on slow
 * 3G a 4 MB ID photo took 89 s, while anything over about 5.5 MB ran past the 120 s upload
 * timeout. What the document reader does with the bytes sets how small is safe:
 *
 * - ID photos and extra documents go to the vision model, which resizes anything over 2048 px
 *   on its long side anyway (flightclaim-ai VISION_MAX_SIDE): 2048 loses nothing.
 * - Boarding passes are read by zxing at full resolution, and a PDF417's modules need about
 *   3 px each (flightclaim-ai PDF_RENDER_DPI). A photo of a printed pass at 2048 px can put
 *   them near 2 px, so a boarding pass keeps 3072 px.
 *
 * Only what can be made smaller is touched: PDFs, .pkpass files, small files, images already
 * within the limit (a PNG screenshot stays lossless) and anything this browser can't decode
 * (HEIC outside Safari) go up exactly as they were. EXIF orientation is applied when the
 * photo is drawn, so the JPEG comes out the right way up with no EXIF of its own.
 */

/** ID photos and extra documents: the vision model's own limit. */
export const DOCUMENT_MAX_SIDE = 2048;
/** Boarding passes (and the default): room for zxing to read a PDF417 from a photo. */
export const BARCODE_MAX_SIDE = 3072;
/** Under this, a file goes up as it is: re-encoding saves seconds at most. */
export const SMALL_FILE_BYTES = 600 * 1024;
export const JPEG_QUALITY = 0.88;
/** A decode that takes longer than this is abandoned, and the original goes up. */
export const DECODE_TIMEOUT_MS = 8000;

/** Image types a browser may decode; everything else (PDF, .pkpass, unknown) is left alone. */
const RASTER = /^image\/(jpeg|png|webp|heic|heif|avif|bmp)$/;
const RASTER_NAME = /\.(jpe?g|png|webp|heic|heif|avif|bmp)$/i;

export interface Decoded {
  /** Oriented size (EXIF rotation applied). */
  width: number;
  height: number;
  /** Draw the oriented image at `width × height` and encode it as JPEG; null if the browser can't. */
  encode: (width: number, height: number, quality: number) => Promise<Blob | null>;
  close: () => void;
}

export interface DownscaleOptions {
  maxSide?: number;
  quality?: number;
  /** Decoder, for tests. Resolves null when the image can't be decoded. */
  decode?: (file: File) => Promise<Decoded | null>;
  timeoutMs?: number;
}

/** The long side scaled to `maxSide`, the short side in proportion. */
export function fitWithin(width: number, height: number, maxSide: number): { width: number; height: number } {
  const scale = maxSide / Math.max(width, height);
  if (scale >= 1) return { width, height };
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

function isRaster(file: File): boolean {
  return RASTER.test(file.type) || (!file.type && RASTER_NAME.test(file.name));
}

function jpegName(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, '');
  return `${base || 'photo'}.jpg`;
}

/**
 * The file to upload: `file` itself, or a JPEG no larger than `maxSide` on its long side when
 * that is smaller. Never rejects: any failure means the original goes up.
 */
export async function downscaleImage(file: File, options: DownscaleOptions = {}): Promise<File> {
  const {
    maxSide = BARCODE_MAX_SIDE,
    quality = JPEG_QUALITY,
    decode = decodeInBrowser,
    timeoutMs = DECODE_TIMEOUT_MS,
  } = options;
  if (!isRaster(file) || file.size < SMALL_FILE_BYTES) return file;
  let image: Decoded | null = null;
  try {
    image = await withTimeout(decode(file), timeoutMs);
    if (!image || !image.width || !image.height) return file;
    if (Math.max(image.width, image.height) <= maxSide) return file;
    const size = fitWithin(image.width, image.height, maxSide);
    const blob = await withTimeout(image.encode(size.width, size.height, quality), timeoutMs);
    if (!blob || !blob.size || blob.size >= file.size) return file;
    return new File([blob], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
  } catch {
    return file;
  } finally {
    image?.close();
  }
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => resolve(null), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(t);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

/**
 * Decode with an <img>: every current browser applies EXIF orientation to it and to
 * drawImage (image-orientation: from-image is the default), and Safari decodes HEIC this way.
 */
async function decodeInBrowser(file: File): Promise<Decoded | null> {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  try {
    await img.decode();
  } catch {
    URL.revokeObjectURL(url);
    return null;
  }
  return {
    width: img.naturalWidth,
    height: img.naturalHeight,
    encode: (width, height, q) => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return Promise.resolve(null);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);
      return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
    },
    close: () => URL.revokeObjectURL(url),
  };
}
